/** A 1-based, inclusive page range. A single page is `start === end`. */
export interface PageRange {
  start: number;
  end: number;
}

export type ParsePageRangesResult =
  | { ranges: PageRange[]; error?: undefined }
  | { ranges?: undefined; error: string };

const TOKEN = /^(\d+)(?:\s*-\s*(\d+))?$/;

/**
 * Parses user input like "1-2, 5, 8-10" into page ranges for a document with
 * `totalPages` pages. Order and duplicates are kept: each range becomes its
 * own output file.
 */
export const parsePageRanges = (
  input: string,
  totalPages: number,
): ParsePageRangesResult => {
  const tokens = input
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  if (!tokens.length) {
    return { error: "Enter at least one page or range, e.g. 1-2, 5." };
  }

  const ranges: PageRange[] = [];
  for (const token of tokens) {
    const match = TOKEN.exec(token);
    if (!match) {
      return { error: `"${token}" is not a valid page or range.` };
    }
    const start = Number(match[1]);
    const end = match[2] === undefined ? start : Number(match[2]);
    if (start < 1 || end > totalPages || start > end) {
      return {
        error: `"${token}" is out of range (document has ${totalPages} page${totalPages === 1 ? "" : "s"}).`,
      };
    }
    ranges.push({ start, end });
  }
  return { ranges };
};
